import { create } from 'zustand';

import { runSync, getLastSyncedAt } from '@/services/sync-service';
import type { SyncOpts } from '@/services/sync-service';
import { uploadPendingPhotos } from '@/services/photo-sync-service';
import { useUserProfileStore } from '@/stores/user-profile-store';
import { refreshProfileSubscription } from '@/stores/auth-store';
import type { SyncStatus, SyncableEntityType } from '@/types/sync';

interface SyncStoreState {
  status: SyncStatus;
  lastSyncedAt: string | null;
  error: string | null;
  /** True once the first initSync call has completed (success or error). Gates InitialSyncBoundary. */
  isInitialSyncComplete: boolean;
  /**
   * M81 Phase 5a — Which entity the sync loop is currently processing.
   * Set at the start of each entity phase; cleared in finally.
   * Used by InitialSyncBoundary to render "Restoring photos..." sub-message.
   */
  currentEntity: SyncableEntityType | null;
  /**
   * M81 Phase 5a — Total photos awaiting on-demand hydration.
   * Set when photo pull completes; decremented as each photo renders successfully.
   * Floored at 0.
   */
  photoHydrationCount: number;
  /** F1.2 — flips to true once the first repo.upsertUser succeeds in the first runSync.
   *  Drives the App.tsx <div data-testid="sync-initial-user-pushed" /> readiness sentinel
   *  the live fixture's storage-state phase waits on (ADR-042 §Layer 3). Fire-once;
   *  never resets after flipping to true. */
  hasPushedFirstUpsert: boolean;
  syncNow: (userId: string) => Promise<void>;
  initSync: (userId: string) => Promise<void>;
  setCurrentEntity: (entity: SyncableEntityType | null) => void;
  setPhotoHydrationCount: (n: number) => void;
  decrementPhotoHydrationCount: () => void;
  markFirstUpsertPushed: () => void;
}

export const useSyncStore = create<SyncStoreState>((set, get) => ({
  status: 'idle',
  lastSyncedAt: null,
  error: null,
  isInitialSyncComplete: false,
  currentEntity: null,
  photoHydrationCount: 0,
  hasPushedFirstUpsert: false,

  /**
   * Trigger a sync cycle. De-bounced: if already syncing, this is a no-op.
   * Sets status to 'offline' and returns early if navigator.onLine is false.
   */
  syncNow: async (userId: string) => {
    if (get().status === 'syncing') return;
    if (!navigator.onLine) {
      set({ status: 'offline' });
      return;
    }

    set({ status: 'syncing', error: null });
    try {
      // Phase 5b (M81): thread instrumentation callbacks so sync-service can update
      // currentEntity and photoHydrationCount without importing sync-store (would be circular).
      const syncOpts: SyncOpts = {
        onEntityStart: (entity) => useSyncStore.getState().setCurrentEntity(entity),
        onPhotoPullCount: (count) => useSyncStore.getState().setPhotoHydrationCount(count),
        onFirstUpsertPushed: () => useSyncStore.getState().markFirstUpsertPushed(),
      };
      // M187 (#1110): gate the cloud round-trip on live Pro entitlement.
      const isPro = useUserProfileStore.getState().isPro();
      const result = await runSync(userId, syncOpts, isPro);
      if (isPro) {
        await uploadPendingPhotos(userId);
      }
      set({ status: 'synced', lastSyncedAt: result.lastSyncedAt, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sync failed';
      console.error('[sync-store] Sync error:', err);
      set({ status: 'error', error: message });
    } finally {
      // Always clear currentEntity when sync completes or errors.
      useSyncStore.getState().setCurrentEntity(null);
    }
  },

  /**
   * Called after auth resolves. Loads lastSyncedAt from Dexie and triggers
   * the first sync. If lastSyncedAt is null (first-time), runSync uses a wide
   * window to push all local data and pull all remote data.
   */
  initSync: async (userId: string) => {
    const lastSyncedAt = await getLastSyncedAt();
    set({ lastSyncedAt });
    // M187 (#1110) bootstrap-race fix: the auth-store session-establishment sites
    // fire refreshProfileSubscription() un-awaited, so `status` flips to
    // 'authenticated' (and App.tsx's initSync effect fires) BEFORE the server tier
    // resolves. Without waiting, syncNow's isPro() gate would evaluate against the
    // ADR-032 Dexie default (tier:'starter') on every brand-new session and no-op
    // the cloud round-trip for a real Pro/beta user. For the FIRST sync only, await
    // the SHARED (in-flight-deduped) refresh so the gate reads the fresh entitlement.
    //
    // Scope fence (M187): only this first-sync gate waits. Subsequent syncNow()
    // calls (foreground/online triggers in App.tsx) are UNAFFECTED. The await
    // tolerates FAILURE (offline/error): on a rejected refresh we fall through and
    // gate against whatever local record exists — no retry-loop, no throw.
    try {
      await refreshProfileSubscription();
    } catch (err) {
      console.warn(
        '[sync-store] initSync: tier refresh failed, gating on local record:',
        err,
      );
    }
    try {
      await get().syncNow(userId);
    } finally {
      // Mark initial sync as complete regardless of success or error,
      // so InitialSyncBoundary always unblocks after the first attempt.
      set({ isInitialSyncComplete: true });
    }
  },

  // M81 Phase 5a — entity-phase observability actions

  /**
   * F1.2 — fire-once gate flipped from sync-service's first-upsert-pushed callback
   * after the first successful repo.upsertUser in the first runSync. Drives the
   * App.tsx <div data-testid="sync-initial-user-pushed" /> readiness sentinel that
   * the live fixture's storage-state phase waits on (ADR-042 §Layer 3).
   * Guarded so subsequent runSync iterations are no-ops (the sentinel must not flicker).
   */
  markFirstUpsertPushed: () => {
    if (!get().hasPushedFirstUpsert) set({ hasPushedFirstUpsert: true });
  },

  /** Set the entity currently being synced. Called at the start of each push/pull phase. */
  setCurrentEntity: (entity: SyncableEntityType | null) => {
    set({ currentEntity: entity });
  },

  /** Set total photos awaiting on-demand hydration after a pull. */
  setPhotoHydrationCount: (n: number) => {
    set({ photoHydrationCount: n });
  },

  /** Decrement photo hydration count by 1 (floors at 0). */
  decrementPhotoHydrationCount: () => {
    set((state) => ({
      photoHydrationCount: Math.max(0, state.photoHydrationCount - 1),
    }));
  },
}));
